/**
 * One-shot DataDog RUM bootstrap for the app shell: org tags (session replay only), config, version, user — then SDK init.
 * Depends on Auth / BusinessLogic; feature code should only import `getDatadogRum` from `./rumClient`.
 */

import * as Auth from "../auth";
import * as BusinessLogic from "../businessLogic";
import { ORG_TAGS_ACCESS } from "../ManufacturingTags.generated";

import { initializeDatadogRum } from "./rumClient";
import { IGetPrivateConfigRes } from "../backend/tracking.generated.types";

/**
 * Checks if the Datadog application id and client token are valid.
 */
function hasDatadogRumCredentials(config: IGetPrivateConfigRes): boolean {
    const appId = config.datadog_app_id.trim();
    const clientToken = config.datadog_client_token.trim();
    return appId.length > 0 && clientToken.length > 0;
}

/** True after the one-shot configure path has been entered (replaces legacy `window.DD_RUM` gating). */
let rumConfigureOnceEntered = false;

/**
 * Called from App after login. At most once per full page load: loads config, then may import
 * the SDK and call `init` / `setUser`.
 * Org tag `user_monitoring_and_session_recording` controls Session Replay sampling only; RUM sessions
 * (views, resources, custom actions/vitals, etc.) still initialize when this tag is off.
 * Skips SDK init when private config has no Datadog application id / client token (e.g. API failure).
 */
export async function configureDatadogRumIfNeeded(): Promise<void> {
    if (rumConfigureOnceEntered) {
        return;
    }

    rumConfigureOnceEntered = true;

    const orgTags = await BusinessLogic.getCurrentOrgTags();
    const isSessionReplayEnabled = ORG_TAGS_ACCESS.user_monitoring_and_session_recording(orgTags);

    const config = await BusinessLogic.getPrivateConfig();
    if (!hasDatadogRumCredentials(config)) {
        return;
    }

    const version = await BusinessLogic.getAppVersion();

    const user = Auth.getLoggedinUser();
    const orgData = Auth.getOrgData();
    const lang = Auth.getUserLang();
    const deploymentEnv = BusinessLogic.getSysFlag("env");

    try {
        await initializeDatadogRum({
            initConfiguration: {
                applicationId: config.datadog_app_id,
                clientToken: config.datadog_client_token,
                site: "datadoghq.eu",
                service: config.datadog_service,
                env: config.installation_name,
                sessionSampleRate: 100,
                version,
                sessionReplaySampleRate: isSessionReplayEnabled ? 100 : 0,
                trackResources: true,
                trackLongTasks: true,
                trackUserInteractions: true,
                defaultPrivacyLevel: "allow",
            },
            user: {
                id: user.uuid,
                name: user.title,
                email: user.username,
                lang,
                org_uuid: orgData.uuid,
                org_code: orgData.code,
                org_title: orgData.title,
            },
            globalContext: {
                deployment_env: deploymentEnv,
            },
        });
    } catch (error) {
        console.error("Datadog RUM initialization failed:", error);
    }
}
